/**
 * resolveLanguage — Cluster C-5 (Feb 06 2026).
 *
 * Single FE source of truth for "what language should this user-typed
 * message be treated as?" Used by drafting hooks, agent chat hooks,
 * Herald compose, and anywhere else the FE must pick between the UI
 * language signal and the actual content of the message.
 *
 * The rule is intentionally simple and matches the backend's
 * detection in `routes/agent.py` _resolve_effective_language:
 *
 *   1. If `text` contains ANY Arabic-script codepoint → 'ar'.
 *      Arabic legal contracts often include English company names,
 *      which should NOT flip the contract language back to English.
 *   2. Else use `uiLanguage` (the value from useLanguage()).
 *   3. Else default to 'en'.
 *
 * Backend rule is unchanged. This module just stops re-implementing
 * the same heuristic in every hook that needs it.
 */

// Arabic + Arabic Supplement + Arabic Extended-A + Arabic Presentation
// Forms-A + Arabic Presentation Forms-B. Matches the backend's
// is_arabic_text regex.
export const ARABIC_SCRIPT_RANGE = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

/** Returns true if any Arabic-script codepoint is in `text`. */
export const containsArabic = (text) => ARABIC_SCRIPT_RANGE.test(text || '');

/**
 * Resolve the effective language for a single piece of user-typed text.
 *
 * @param {string} text         The message content as the user typed it.
 * @param {string} [uiLanguage] The current UI language ('en' | 'ar').
 *                              Pass the value from `useLanguage().language`.
 * @returns {'en' | 'ar'}
 */
export const resolveLanguage = (text, uiLanguage) => {
  if (containsArabic(text)) return 'ar';
  if (uiLanguage === 'ar' || uiLanguage === 'en') return uiLanguage;
  return 'en';
};

export default resolveLanguage;
